import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from "../../tooltip/tooltip";
import { instanceMob } from "../../../utils/utils";
import { useModalManager } from "./utils/useModalManager";
import UnassignedUsersModal from "./utils/unassigned-users-modal";
import UsersByPlanDetailModal from "./utils/users-by-plan-detail-modal";
import PlanFormModal from "./utils/plan-form-modal";
import InlineModal from "../../info-modal/infoModal";
import BiometricCaptureModal from "./utils/biometric-capture-modal";
import debounce from "lodash.debounce";
import './styles.css';

const UsersByPlanPanel = () => {
    const [usersNoPlan, setUsersNoPlan] = useState(null);
    const [usersExpired, setUsersExpired] = useState({});
    const [userNearExpired, setUserNearExpired] = useState([]);
    const [userFreeze, setUserFreeze] = useState([]);
    const [userActive, setUserActive] = useState([]);
    const [users, setUsers] = useState({ results: [], next: null, previous: null, count: 0 });
    const [activeFilter, setActiveFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalMode, setModalMode] = useState(null);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [lastUrl, setLastUrl] = useState(null);
    const totalPages = Math.ceil(users.count / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + users.results.length;

    const { activeModal, modalData, openModal, closeModal } = useModalManager();

    const getPageNumberFromUrl = (url) => {
        if (!url) return null;
        const params = new URL(url).searchParams;
        return parseInt(params.get('page') || 1, 10);
    };

    const getUsersNoPlan = () => {
        instanceMob.get('admin/customer/noplan')
            .then((res) => setUsersNoPlan(res.data))
            .catch((err) => console.error(err));
    };

    const getUsersExpired = () => {
        instanceMob
            .get(`admin/membership?filter=expired`)
            .then((res) => res.data)
            .then((data) => {
                setUsersExpired(data);
            })
            .catch((err) => console.error(err));
    }

    const getUsersNearExpired = () => {
        instanceMob
            .get(`admin/membership?filter=near_to_expire`)
            .then((res) => res.data)
            .then((data) => {
                setUserNearExpired(data);
            })
            .catch((err) => console.error(err));
    }

    const getUsersFreeze = () => {
        instanceMob
            .get(`admin/membership?filter=freeze`)
            .then((res) => res.data)
            .then((data) => {
                setUserFreeze(data);
            })
            .catch((err) => console.error(err));
    }

    const getUsersActive = () => {
        instanceMob
            .get(`admin/membership?filter=active`)
            .then((res) => res.data)
            .then((data) => {
                setUserActive(data);
            })
            .catch((err) => console.error(err));
    }

    const getUsers = (url = null, search = '', filter = null) => {
        const finalUrl = url
            ?? `admin/membership?search=${encodeURIComponent(search)}${filter ? `&filter=${filter}` : ''}`;

        instanceMob.get(finalUrl)
            .then(({ data }) => {
                setUsers(data);
                setLastUrl(finalUrl);
                if (!url) setPageSize(10);
                setCurrentPage(getPageNumberFromUrl(finalUrl) || 1);
            })
            .catch(console.error);
    };

    const deleteUser = async () => {
        const membership_id = modalData?.memberships[0]?.membership_id;
        if (!membership_id) return;

        try {
            await instanceMob.delete(`admin/membership/${membership_id}/delete`);
            setCurrentPage(1);
            await getUsers(null, searchTerm, activeFilter);
            await getUsersNoPlan();
            closeModal();
        } catch (error) {
            console.error("Error al eliminar la membresía:", error);
        }
    };

    const handlePrevPage = () => {
        if (users.previous) {
            getUsers(`${users.previous}&search=${encodeURIComponent(searchTerm)}`, searchTerm, activeFilter);
        }
    };

    const handleNextPage = () => {
        if (users.next) {
            getUsers(`${users.next}&search=${encodeURIComponent(searchTerm)}`, searchTerm, activeFilter);
        }
    };

    const debouncedSearch = useMemo(() => {
        return debounce((value, filter) => {
            getUsers(null, value, filter);
        }, 600);
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);
        debouncedSearch(value, activeFilter);
    };

    const handleFilterChange = (filterKey) => {
        setIsStatusMenuOpen(false);
        const newFilter = activeFilter === filterKey ? null : filterKey;
        setActiveFilter(newFilter);
        setCurrentPage(1);
        getUsers(null, searchTerm, newFilter);
    };

    const resetState = () => {
        setUsersNoPlan(null);
        setUsersExpired({});
        setUserNearExpired([]);
        setUserFreeze([]);
        setUserActive([]);
        setUsers({ results: [], next: null, previous: null, count: 0 });
        setActiveFilter(null);
        setCurrentPage(1);
        setPageSize(10);
        setSearchTerm('');
        setModalMode(null);
        setIsStatusMenuOpen(false);
    };

    const handleViewMore = () => {
        openModal('UnassignedUsersModal', usersNoPlan?.results || null);
    };

    const handleViewDetail = (user) => {
        openModal('UsersByPlanDetailModal', user);
    };

    const handleFormModal = (user, mode) => {
        setModalMode(mode)
        openModal('PlanFormModal', user);
    };

    const handleDeleteModal = (user) => {
        openModal('DeleteModal', user);
    };

    const handleBiometricModal = (user) => {
        openModal('BiometricRegistrationModal', user);
    };

    const downloadExpiredCustomersExcel = () => {
        instanceMob
            .get('admin/membership/expired_customers/excel', {
                responseType: 'blob',
            })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;

                link.setAttribute('download', 'expired_customers.xlsx');

                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => console.error('Error descargando Excel:', err));
    };

    useEffect(() => {
        resetState();
        getUsersNoPlan();
        getUsersExpired();
        getUsersNearExpired();
        getUsersFreeze();
        getUsersActive();
        getUsers();

        return () => {
            resetState();
        };
    }, []);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <>
            <div
                className="users-plan-panel-container w-100 h-100 p-3 d-flex flex-column justify-content-start align-self-start gap-3">
                <div className="users-plan-panel-top d-flex flex-row align-items-center justify-content-between px-3">
                    <div className="d-flex flex-row gap-3 align-items-center">
                        <div className="d-flex flex-row gap-2">
                            <span className="users-plan-panel-title">
                                Usuarios sin asignar
                            </span>
                            <span
                                className="users-plan-panel-count">{usersNoPlan?.count ? "0" + usersNoPlan.count : "000"}</span>
                        </div>
                        <button className="users-plan-panel-button button btn btn-primary" onClick={handleViewMore}>Ver
                            más
                        </button>
                    </div>

                    <div className="users-plan-panel-title fw-medium">
                        Usuarios por plan
                    </div>

                    <div className="d-flex flex-row gap-1">
                        <button
                            className="users-plan-panel-button button btn btn-primary"
                            onClick={() => {
                                handleFormModal(null, "assign")
                            }}
                        >
                            Asignar Plan +
                        </button>
                        <button
                            className="users-plan-panel-button-download button btn btn-primary"
                            onClick={downloadExpiredCustomersExcel}
                        >
                            Descargar base de datos
                        </button>
                    </div>
                </div>

                <div
                    className="users-plan-panel-actions d-flex flex-row gap-3 align-items-center justify-content-center mt-5">
                    <input
                        type="text"
                        className="plans-list-panel-input"
                        placeholder="Buscar usuario"
                        aria-label="Buscar usuario"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <div
                        className={`users-plan-panel-chip gap-1 ${activeFilter === null ? 'active' : ''}`}
                        onClick={() => handleFilterChange(null)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13"
                            fill="none">
                            <path
                                d="M9.49635 3.36343C9.50306 4.97845 8.20594 6.28031 6.58106 6.28886C4.96766 6.29739 3.66004 4.99403 3.65626 3.37353C3.65247 1.75177 4.94764 0.452529 6.57093 0.449225C8.19276 0.445891 9.48951 1.73762 9.49635 3.36343Z"
                                fill="currentColor" />
                            <path
                                d="M6.65974 12.6862C5.39277 12.6862 4.12593 12.686 2.85908 12.6863C2.46858 12.6864 2.08918 12.6329 1.74482 12.4343C1.21418 12.1283 0.956492 11.646 0.941721 11.0533C0.914866 9.98001 1.01008 8.91853 1.40327 7.90481C1.55696 7.50831 1.76789 7.14329 2.06965 6.83899C2.43293 6.47238 2.88691 6.31395 3.39326 6.2892C3.44001 6.28692 3.4936 6.31363 3.53584 6.3399C3.74397 6.46907 3.94868 6.60339 4.15534 6.73463C5.02473 7.28662 5.96882 7.56979 7.00251 7.49118C7.76277 7.43332 8.46711 7.18851 9.11494 6.78503C9.34919 6.63898 9.57795 6.4839 9.8144 6.34176C9.87153 6.30742 9.95088 6.29161 10.0185 6.29538C10.8332 6.34057 11.3748 6.7877 11.7486 7.48007C12.1093 8.14757 12.263 8.87456 12.3466 9.61723C12.3925 10.0255 12.4082 10.4387 12.4132 10.8499C12.4271 11.9648 11.7493 12.669 10.632 12.6809C9.30805 12.6949 7.98384 12.6842 6.65974 12.6842V12.6862Z"
                                fill="currentColor" />
                        </svg>
                        Usuarios
                    </div>
                    <div className={`users-plan-panel-chip gap-1 ${activeFilter === 'expired' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('expired')}>
                        <span className="users-plan-panel-chip-count">{usersExpired?.count || 0}</span>
                        Vencidos
                    </div>
                    <div className={`users-plan-panel-chip gap-1 ${activeFilter === 'near_to_expire' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('near_to_expire')}>
                        <span className="users-plan-panel-chip-count">{userNearExpired?.count || 0}</span>
                        Próximos a vencer
                    </div>
                    <div className={`users-plan-panel-chip gap-1 ${activeFilter === 'freeze' ? 'active' : ''}`}
                        onClick={() => handleFilterChange('freeze')}>
                        Congelaciones
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                            <path
                                d="M7.89209 16.7059C7.82498 16.6914 7.75555 16.6835 7.69104 16.6613C7.40118 16.5617 7.21136 16.2928 7.21109 15.9865C7.21063 15.5083 7.211 15.03 7.211 14.5518V14.4962C7.19275 14.5134 7.18188 14.5231 7.17154 14.5335C6.88134 14.8235 6.59094 15.1133 6.30124 15.4038C6.14621 15.5592 5.96069 15.6363 5.74057 15.628C5.32327 15.6122 5.00055 15.2126 5.07202 14.8004C5.09912 14.644 5.16894 14.5107 5.28155 14.3983C5.91485 13.766 6.54739 13.133 7.18069 12.5007C7.20233 12.4792 7.21164 12.4577 7.21161 12.4269C7.21081 11.6576 7.211 10.8883 7.211 10.119V10.0716C7.19525 10.0792 7.18499 10.0833 7.17547 10.0888C6.55163 10.4488 5.92788 10.8091 5.30371 11.1685C5.2785 11.1831 5.26449 11.2013 5.25543 11.2285C4.94265 12.1683 4.62961 13.108 4.31576 14.0474C4.21579 14.3466 3.97964 14.5308 3.67105 14.5535C3.33618 14.5781 3.01523 14.3414 2.93658 14.0145C2.90191 13.8703 2.9118 13.7303 2.95849 13.5908C3.09964 13.1692 3.23993 12.7474 3.38049 12.3257C3.38562 12.3103 3.39016 12.2949 3.39795 12.2699C3.36493 12.2886 3.34063 12.3022 3.31656 12.3161C2.91363 12.5487 2.51196 12.7836 2.10736 13.0132C1.84192 13.1637 1.57708 13.1529 1.32419 12.981C0.990506 12.7542 0.924588 12.2615 1.18237 11.951C1.24551 11.875 1.32071 11.8148 1.40594 11.7657C1.87121 11.4975 2.33624 11.2289 2.80133 10.9604C2.81122 10.9547 2.82074 10.9484 2.83767 10.938C2.81723 10.9329 2.80343 10.9292 2.78949 10.9261C2.30929 10.8193 1.82913 10.7121 1.34875 10.606C1.02786 10.5351 0.806912 10.2945 0.769956 9.97307C0.727597 9.60493 1.00103 9.25061 1.36844 9.19612C1.47015 9.18104 1.57003 9.18822 1.66986 9.21045C2.47271 9.38914 3.2756 9.56741 4.07843 9.74585C4.26727 9.78783 4.45599 9.83041 4.64508 9.87118C4.66208 9.87486 4.68457 9.87248 4.69934 9.86398C5.29364 9.52199 5.88736 9.17897 6.48111 8.83604C6.48544 8.83353 6.48932 8.83018 6.49746 8.82429C6.48675 8.8168 6.47851 8.81012 6.46942 8.80485C5.88345 8.4664 5.29751 8.12787 4.71087 7.7905C4.69543 7.7816 4.67157 7.77978 4.65365 7.78374C4.14328 7.89635 3.63321 8.01022 3.12301 8.12363C2.63647 8.23176 2.14993 8.33958 1.66342 8.44765C1.41681 8.50246 1.19577 8.45303 1.00348 8.28607C0.679807 8.00499 0.692319 7.46956 1.02725 7.20178C1.13226 7.11787 1.25168 7.07152 1.38134 7.04312C1.8569 6.93889 2.33203 6.83269 2.80728 6.72719C2.81854 6.72468 2.82959 6.72143 2.84906 6.71637C2.83169 6.70558 2.81973 6.69763 2.80737 6.69046C2.33886 6.42009 1.87051 6.14972 1.40191 5.87956C1.20663 5.76694 1.07821 5.60316 1.03967 5.38002C0.99017 5.09334 1.08148 4.85194 1.31387 4.67805C1.54739 4.50334 1.80847 4.47774 2.06695 4.615C2.35998 4.7706 2.64377 4.9435 2.93149 5.10905C3.0748 5.19151 3.21789 5.27434 3.36111 5.35696C3.36877 5.36137 3.37674 5.3652 3.39151 5.37292C3.38763 5.35689 3.38592 5.34672 3.38272 5.33708C3.24566 4.92634 3.10995 4.51517 2.97055 4.10524C2.91327 3.93664 2.89108 3.76851 2.95062 3.59703C3.043 3.33118 3.22827 3.16582 3.5043 3.11447C3.86389 3.04755 4.19638 3.25252 4.315 3.60701C4.56418 4.3519 4.81204 5.09723 5.06045 5.84238C5.12491 6.03563 5.18881 6.22905 5.25488 6.42171C5.26156 6.44125 5.27804 6.46217 5.29577 6.47247C5.92117 6.83494 6.54727 7.19626 7.17328 7.55768C7.18301 7.56328 7.19317 7.56816 7.20865 7.57625C7.20956 7.55818 7.21084 7.54489 7.21084 7.53165C7.211 6.76364 7.21078 5.99564 7.21164 5.22761C7.21167 5.19623 7.20028 5.17566 7.17904 5.15449C6.54669 4.52309 5.91488 3.89111 5.28283 3.2594C4.99542 2.97214 4.98837 2.53877 5.26684 2.24307C5.52075 1.9734 5.97744 1.95171 6.24817 2.20607C6.46859 2.41319 6.67852 2.63141 6.89294 2.84486C6.98745 2.93893 7.08157 3.03342 7.1759 3.12761C7.18463 3.13631 7.19406 3.14419 7.211 3.15954V3.10652C7.211 2.62956 7.21081 2.15262 7.21106 1.67566C7.21115 1.36111 7.40265 1.09329 7.70007 0.990859C8.13702 0.840392 8.61438 1.15829 8.63934 1.61833C8.64544 1.73213 8.64389 1.84639 8.64404 1.96046C8.64453 2.34126 8.64422 2.72206 8.64422 3.10284V3.15889C8.66098 3.14307 8.67166 3.13345 8.68185 3.12329C8.97107 2.83417 9.26074 2.54539 9.54937 2.25563C9.7041 2.10031 9.88846 2.02054 10.1089 2.02873C10.5299 2.04436 10.8521 2.44101 10.7814 2.85626C10.7549 3.01152 10.6858 3.1443 10.5744 3.25563C9.94326 3.88641 9.31244 4.51755 8.68069 5.14778C8.65411 5.17428 8.64346 5.20059 8.64352 5.23808C8.64447 6.00224 8.64422 6.76639 8.64422 7.53055V7.58462C8.65985 7.57692 8.67007 7.57259 8.67956 7.56711C9.30237 7.20773 9.92495 6.84813 10.548 6.48933C10.5745 6.4741 10.5891 6.45503 10.5986 6.42647C10.9113 5.48668 11.2244 4.54703 11.5382 3.6076C11.6378 3.30955 11.8745 3.1259 12.1842 3.10316C12.5164 3.07875 12.8376 3.31552 12.9165 3.64044C12.9517 3.78583 12.9415 3.92715 12.8943 4.06792C12.753 4.48939 12.6127 4.91118 12.4721 5.33292C12.4673 5.3472 12.4634 5.3618 12.4561 5.38614C12.4762 5.37503 12.4889 5.36832 12.5013 5.36114C12.912 5.12408 13.3223 4.88617 13.7336 4.65018C14.0023 4.49606 14.2693 4.50038 14.5271 4.67436C14.8629 4.90095 14.9295 5.39476 14.6703 5.70685C14.6081 5.78183 14.5339 5.84137 14.4498 5.88986C13.9834 6.15872 13.5173 6.42798 13.0511 6.69713C13.0413 6.70277 13.0319 6.70898 13.0149 6.71956C13.0757 6.7332 13.1288 6.74518 13.1819 6.75697C13.6258 6.85565 14.0699 6.95369 14.5137 7.05311C14.829 7.12378 15.044 7.36221 15.0832 7.68165C15.128 8.04703 14.8519 8.40637 14.4869 8.46077C14.3636 8.47914 14.2447 8.46062 14.1252 8.43397C13.1561 8.21815 12.1869 8.00282 11.2175 7.78863C11.1952 7.78371 11.1656 7.78699 11.1461 7.79814C10.567 8.13071 9.98883 8.46475 9.41052 8.79858C9.3985 8.80551 9.3869 8.81316 9.36993 8.82368C9.38641 8.83397 9.39865 8.84206 9.41131 8.84936C9.99514 9.1864 10.579 9.52353 11.1634 9.85957C11.179 9.86853 11.2026 9.87157 11.2203 9.86766C12.2097 9.64897 13.199 9.42947 14.1882 9.20935C14.3563 9.17197 14.5182 9.18309 14.6763 9.25502C15.1222 9.45799 15.227 10.0739 14.8728 10.4125C14.768 10.5128 14.6448 10.5753 14.5035 10.6064C14.0206 10.7128 13.538 10.8207 13.0552 10.9281C13.0493 10.9294 13.0433 10.9311 13.0282 10.9351C13.0453 10.946 13.056 10.9533 13.0673 10.9598C13.5257 11.2245 13.9838 11.4897 14.4429 11.7531C14.5578 11.8191 14.6606 11.8963 14.7367 12.0071C15.0394 12.4481 14.7534 13.0651 14.2207 13.1194C14.0623 13.1356 13.915 13.102 13.7777 13.0229C13.3569 12.7803 12.9362 12.5373 12.5155 12.2946C12.4971 12.284 12.4785 12.2736 12.45 12.2575C12.461 12.2898 12.4684 12.3111 12.4755 12.3324C12.6137 12.7469 12.7505 13.1617 12.8905 13.5755C12.9554 13.7673 12.9556 13.9545 12.8721 14.1408C12.7292 14.4593 12.3497 14.63 12.0166 14.5242C11.7753 14.4476 11.6174 14.2862 11.5378 14.0477C11.2378 13.1496 10.9386 12.2512 10.639 11.353C10.6196 11.2947 10.6115 11.2267 10.5753 11.1817C10.5399 11.1378 10.4755 11.1171 10.4233 11.0871C9.84289 10.7518 9.26233 10.4167 8.68179 10.0816C8.67209 10.076 8.66183 10.0713 8.64657 10.0635C8.64566 10.0811 8.64431 10.0944 8.64431 10.1076C8.64422 10.8833 8.64441 11.659 8.64355 12.4347C8.64352 12.4635 8.65405 12.4819 8.67321 12.501C9.30551 13.1325 9.93817 13.7636 10.5689 14.3967C10.7807 14.6093 10.8471 14.8646 10.7478 15.1488C10.6485 15.4332 10.4369 15.5927 10.1389 15.6249C9.91119 15.6496 9.71621 15.5677 9.55459 15.406C9.26544 15.1167 8.9761 14.8277 8.68722 14.5382C8.67578 14.5267 8.66848 14.5111 8.65927 14.4974C8.65426 14.5006 8.64923 14.5037 8.64422 14.5069V14.5506C8.64422 15.0186 8.64428 15.4866 8.64422 15.9546C8.64416 16.2081 8.54974 16.4158 8.34375 16.5682C8.23919 16.6456 8.12042 16.6866 7.99151 16.6998C7.98135 16.7008 7.9714 16.7038 7.96133 16.7059H7.89209Z"
                                fill="currentColor" />
                        </svg>
                    </div>
                </div>

                <div className="users-plan-panel-table w-100">
                    <div className="users-plan-panel-scroll-body table-responsive">
                        <table className="table table-sm table-hover table-borderless align-middle mb-0">
                            <thead className="sticky-top bg-white">
                                <tr>
                                    <th className="users-plan-panel-table-labels text-start align-content-center"
                                        style={{ width: '25%' }}>
                                        Nombre
                                    </th>
                                    <th
                                        className="users-plan-panel-table-labels text-center align-content-center"
                                        style={{ width: '10%' }}
                                    >
                                        <div className="dropdown position-relative">
                                            <div
                                                className="users-plan-panel-table-labels dropdown-toggle d-flex align-items-center justify-content-center cursor-pointer gap-1"
                                                onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}
                                            >
                                                Estado
                                                {isStatusMenuOpen ? (
                                                    <i
                                                        className={`bi bi-caret-up-fill ${activeFilter ? 'icon-active-bounce' : ''}`}
                                                    ></i>
                                                ) : (
                                                    <i
                                                        className={`bi bi-caret-down-fill ${activeFilter ? 'icon-active-bounce' : ''}`}
                                                    ></i>
                                                )}
                                            </div>

                                            {isStatusMenuOpen && (
                                                <div
                                                    className={`show users-plan-panel-table-dropdown-menu d-flex flex-column`}
                                                >
                                                    <div
                                                        onClick={() => handleFilterChange('active')}
                                                        className={`users-plan-panel-table-dropdown-menu-label cursor-pointer p-1 ${activeFilter === 'active' ? 'active' : ''}`}>
                                                        Vigentes
                                                    </div>

                                                    <div
                                                        onClick={() => handleFilterChange('near_to_expire')}
                                                        className={`users-plan-panel-table-dropdown-menu-label cursor-pointer p-1 ${activeFilter === 'near_to_expire' ? 'active' : ''}`}>
                                                        P. a vencer
                                                    </div>

                                                    <div
                                                        onClick={() => handleFilterChange('expired')}
                                                        className={`users-plan-panel-table-dropdown-menu-label cursor-pointer p-1 ${activeFilter === 'expired' ? 'active' : ''}`}>
                                                        Vencido
                                                    </div>

                                                    <div
                                                        onClick={() => handleFilterChange('freeze')}
                                                        className={`users-plan-panel-table-dropdown-menu-label cursor-pointer p-1 ${activeFilter === 'freeze' ? 'active' : ''}`}>
                                                        Congelado
                                                    </div>

                                                    <div
                                                        onClick={() => handleFilterChange('pending_renew')}
                                                        className={`users-plan-panel-table-dropdown-menu-label cursor-pointer p-1 ${activeFilter === 'pending_renew' ? 'active' : ''}`}>
                                                        P. Renovar
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th className="users-plan-panel-table-labels text-start align-content-center"
                                        style={{ width: '20%' }}>
                                        Plan
                                    </th>
                                    <th className="users-plan-panel-table-labels text-start align-content-center"
                                        style={{ width: '10%' }}>
                                        Congelaciones restantes
                                    </th>
                                    <th className="users-plan-panel-table-labels text-center align-content-center"
                                        style={{ width: '10%' }}>
                                        Detalles
                                    </th>
                                    <th className="users-plan-panel-table-labels text-start align-content-center"
                                        style={{ width: '10%' }}>
                                        Vencimiento
                                    </th>
                                    <th className="users-plan-panel-table-labels text-center align-content-center"
                                        style={{ width: '15%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.results?.map((user, index) => {
                                    const membership = user?.memberships[0] || {};
                                    const flag = membership?.flag;

                                    const statusMap = {
                                        10: { label: 'Vigente', className: 'users-plan-panel-status-cell-success' },
                                        20: { label: 'P. a vencer', className: 'users-plan-panel-status-cell-warning' },
                                        30: { label: 'Vencido', className: 'users-plan-panel-status-cell-danger' },
                                        40: { label: 'Congelado', className: 'users-plan-panel-status-cell-info' },
                                        50: { label: 'P. Renovar', className: 'users-plan-panel-status-cell-pending' }
                                    };

                                    const status = statusMap[flag] || {
                                        label: 'Desconocido',
                                        className: 'users-plan-panel-status-cell-default'
                                    };

                                    const endDate = membership?.membership_end_date
                                        ? new Date(membership.membership_end_date).toLocaleDateString('es-CO')
                                        : 'N/A';

                                    return (
                                        <tr key={index}>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-start">
                                                {user?.customer_name}
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-center">
                                                <div
                                                    className={`users-plan-panel-status-cell ${status.className}`}>
                                                    {status.label}
                                                </div>
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-start">
                                                {membership?.plan_name || 'Sin Plan'}
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-center">
                                                {membership?.membership_remaining_freezes ?? 0}
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-center">
                                                <span data-placement="top" title="Ver detalle"
                                                    onClick={() => handleViewDetail(user)}>
                                                    <svg className="cursor-pointer" xmlns="http://www.w3.org/2000/svg"
                                                        width="20" height="20" viewBox="0 0 22 22" fill="none">
                                                        <circle cx="10.3685" cy="11.1419" r="10.2747" fill="#B5C1CD" />
                                                        <path
                                                            d="M11.2359 13.4657C10.882 13.6617 10.5212 13.8131 10.137 13.9015C8.75478 14.2181 7.49367 13.9542 6.37746 13.0777C5.53725 12.4171 5.00069 11.5497 4.81984 10.4981C4.53371 8.83408 5.03908 7.43101 6.32368 6.32627C6.93544 5.80064 7.65382 5.47837 8.45247 5.37852C10.1013 5.17222 11.4657 5.71567 12.5172 7.00669C12.9853 7.58164 13.2666 8.24863 13.3822 8.98032C13.536 9.95371 13.3822 10.8797 12.9133 11.7501C12.8827 11.807 12.8867 11.8382 12.9323 11.8841C13.7434 12.692 14.5507 13.5033 15.3634 14.3104C15.6084 14.5526 15.7554 14.8346 15.759 15.182C15.7638 15.6427 15.479 16.0758 15.0619 16.2533C14.6143 16.4439 14.1253 16.3536 13.7742 16.006C13.2872 15.5238 12.8043 15.0375 12.3199 14.5526C11.9597 14.1922 11.5999 13.8309 11.2359 13.4657ZM9.08266 12.8717C10.8646 12.8609 12.2691 11.4435 12.2771 9.6959C12.2855 7.91733 10.8596 6.50962 9.12343 6.4918C7.3553 6.47349 5.91481 7.88535 5.90016 9.65147C5.88551 11.4229 7.29811 12.8551 9.08266 12.8717Z"
                                                            fill="white" />
                                                        <path
                                                            d="M9.03371 7.35214C9.40395 7.33725 9.63667 7.61313 9.66615 7.88363C9.6993 8.18686 9.45992 8.48666 9.15474 8.5162C9.01692 8.52914 8.87508 8.53622 8.74397 8.57553C8.26235 8.72055 7.95204 9.139 7.93068 9.65121C7.91866 9.94125 7.77046 10.1539 7.52645 10.2325C7.149 10.3541 6.76711 10.0728 6.76564 9.6727C6.76186 8.7059 7.39137 7.81479 8.30843 7.49399C8.42598 7.45273 8.54762 7.4205 8.66994 7.39706C8.78957 7.37362 8.91237 7.3663 9.03371 7.35214Z"
                                                            fill="white" />
                                                    </svg>
                                                </span>
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-start">
                                                {endDate}
                                            </td>
                                            <td className="plans-list-panel-name-cell users-plan-panel-table-labels text-center">
                                                <div className="d-flex flex-row justify-content-center align-items-center gap-2">
                                                    <Tooltip title="Renovar">
                                                        <svg
                                                            onClick={() => {
                                                                handleFormModal(user, "renew")
                                                            }}
                                                            className="bi cursor-pointer"
                                                            xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                            viewBox="0 0 20 20" fill="none">
                                                            <circle cx="10.4464" cy="10.4737" r="9.60652"
                                                                fill="#394F65" />
                                                            <path
                                                                d="M12.7216 6.50035C12.6927 6.33213 12.6679 6.19077 12.6439 6.04871C12.5902 5.73136 12.7761 5.45712 13.0786 5.40623C13.3669 5.35676 13.627 5.54689 13.6829 5.85858C13.7903 6.45935 13.8942 7.06154 13.9953 7.66372C14.0497 7.98743 13.861 8.24683 13.5394 8.30125C12.9372 8.40444 12.335 8.50693 11.7328 8.61012C11.4325 8.661 11.1688 8.47936 11.1102 8.1818C11.0565 7.90615 11.2367 7.63757 11.5279 7.57538C11.7484 7.52873 11.9717 7.49622 12.1944 7.45663L12.2064 7.41352C11.9477 7.31316 11.6947 7.19017 11.4289 7.11667C9.40256 6.55406 7.35074 7.94432 7.11113 10.0343C6.89908 11.8797 8.21797 13.5909 10.0358 13.8291C11.9216 14.0764 13.5874 12.8544 13.9218 10.9793C13.9811 10.6428 14.2257 10.4485 14.5254 10.4994C14.8434 10.5524 15.0208 10.816 14.9636 11.1588C14.8123 12.0642 14.4257 12.8558 13.7889 13.5174C12.8489 14.4934 11.6918 14.9613 10.3398 14.9161C8.20453 14.844 6.40363 13.2269 6.08699 11.1185C5.74278 8.82145 7.14365 6.6721 9.37428 6.08688C10.491 5.79356 11.5674 5.91371 12.598 6.43815C12.6305 6.45511 12.663 6.47066 12.7216 6.50035Z"
                                                                fill="white" />
                                                        </svg>
                                                    </Tooltip>

                                                    <Tooltip title="Editar">
                                                        <svg
                                                            onClick={() => {
                                                                handleFormModal(user, "edit")
                                                            }}
                                                            className="bi cursor-pointer" width="20" height="20"
                                                            viewBox="0 0 20 20" fill="none"
                                                            xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M8.57275 19.5251C6.67188 19.5251 4.77087 19.5255 2.87012 19.5251C1.56775 19.5243 0.691895 18.6498 0.691772 17.349C0.691284 13.5402 0.691284 9.73258 0.691772 5.92423C0.691772 4.62076 1.56616 3.74747 2.86902 3.74698C5.87024 3.74649 8.87183 3.74576 11.8733 3.75089C12.0455 3.75089 12.1621 3.69888 12.2821 3.57852C13.1309 2.71915 13.9868 1.86734 14.8413 1.01407C15.777 0.0802326 16.9954 0.0785236 17.9358 1.00968C18.3673 1.43692 18.7968 1.86685 19.2246 2.29776C20.1355 3.21695 20.1349 4.44644 19.2229 5.36222C18.3605 6.22721 17.4976 7.09268 16.6283 7.95157C16.511 8.06729 16.4698 8.1835 16.47 8.34415C16.4744 11.3669 16.4747 14.3888 16.4724 17.4115C16.4716 18.4079 15.8688 19.2108 14.9323 19.4528C14.7281 19.506 14.5094 19.5219 14.2969 19.5226C12.3885 19.5273 10.4808 19.5251 8.57275 19.5251ZM15.0995 9.49991C15.0109 9.58365 14.9547 9.63394 14.9009 9.68717C13.198 11.3888 11.4979 13.0946 9.78723 14.789C9.63831 14.9364 9.42871 15.0563 9.22644 15.1095C7.6405 15.5304 6.04932 15.9323 4.45923 16.3373C4.34998 16.3647 4.23132 16.3857 4.12122 16.373C3.64917 16.318 3.40076 15.8808 3.56702 15.3974C4.07568 13.9128 4.58447 12.4281 5.10657 10.9486C5.17261 10.7609 5.28772 10.5727 5.42761 10.432C7.12415 8.72281 8.82874 7.02262 10.5316 5.31998C10.5851 5.26676 10.636 5.21036 10.7207 5.12003H10.474C7.93701 5.12003 5.40051 5.12003 2.86353 5.12076C2.32312 5.12076 2.06567 5.38053 2.06567 5.92423C2.06531 9.73258 2.06531 13.5402 2.06567 17.3483C2.06567 17.8876 2.32776 18.151 2.86523 18.151C6.67419 18.1515 10.4828 18.1515 14.2916 18.1515C14.8442 18.151 15.0989 17.8937 15.0991 17.3346C15.0995 14.8053 15.0995 12.2763 15.0995 9.74771C15.0995 9.67935 15.0995 9.61124 15.0995 9.49991ZM14.4574 3.44986C11.9038 6.00235 9.33569 8.56998 6.78296 11.1212C7.55078 11.8898 8.33704 12.6759 9.0968 13.4369C11.6561 10.8786 14.2239 8.31119 16.7711 5.7648C16.0028 4.99552 15.2169 4.20963 14.4574 3.44986ZM6.03674 12.4691C5.78784 13.1925 5.53552 13.9254 5.2666 14.7065C6.10217 14.4926 6.88745 14.2914 7.66003 14.0941C7.11121 13.5448 6.57971 13.0126 6.03674 12.4691Z"
                                                                fill="#9DACBD" />
                                                        </svg>
                                                    </Tooltip>

                                                    <Tooltip title="Registro Biométrico">
                                                        <i
                                                            className="bi bi-person-bounding-box biometric-action-icon cursor-pointer"
                                                            onClick={() => handleBiometricModal(user)}
                                                        >
                                                        </i>
                                                    </Tooltip>

                                                    <Tooltip title="Eliminar">
                                                        <svg
                                                            onClick={() => {
                                                                handleDeleteModal(user)
                                                            }}
                                                            className="bi bi-trash3-fill"
                                                            xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                            fill="#B5C1CD" viewBox="0 0 16 16">
                                                            <path
                                                                d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
                                                        </svg>
                                                    </Tooltip>
                                                </div>

                                            </td>
                                        </tr>
                                    );
                                })}
                                {users.count === 0 && (<tr>
                                    <td colSpan="7"
                                        className="plans-list-panel-table-labels text-center text-muted py-3">
                                        No se encontraron resultados.
                                    </td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex justify-content-end align-items-center gap-4 w-75 mt-3 ms-auto">
                        <span className="plans-list-panel-table-labels">
                            {users.count > 0
                                ? `${startIndex + 1} - ${Math.min(endIndex, users.count)} de ${users.count}`
                                : '0 de 0'}
                        </span>
                        <div className="d-flex gap-2">

                            <svg
                                className={`bi bi-chevron-left ${currentPage === 1 ? 'text-muted' : 'cursor-pointer'}`}
                                onClick={currentPage === 1 ? undefined : handlePrevPage}
                                xmlns="http://www.w3.org/2000/svg" width="9" height="15"
                                viewBox="0 0 9 15" fill="none"
                                style={{ pointerEvents: currentPage === 1 ? 'none' : 'auto' }}
                            >
                                <path
                                    d="M0.0423059 7.30351C0.0423059 6.92015 0.188535 6.53675 0.481072 6.24419L6.28647 0.438753C6.87147 -0.146251 7.81998 -0.146251 8.405 0.438753C8.99 1.02376 8.99 1.97231 8.405 2.55733L3.65881 7.30351L8.405 12.0497C8.99 12.6347 8.99 13.5832 8.405 14.1683C7.81998 14.7533 6.87147 14.7533 6.28647 14.1683L0.481072 8.36282C0.188535 8.0703 0.0423059 7.68685 0.0423059 7.30351Z"
                                    fill="#9DACBD" />
                            </svg>


                            <svg
                                className={`bi bi-chevron-right ${currentPage === totalPages || totalPages === 0 ? 'text-muted' : 'cursor-pointer'}`}
                                onClick={currentPage === totalPages || totalPages === 0 ? undefined : handleNextPage}
                                xmlns="http://www.w3.org/2000/svg" width="10" height="15"
                                viewBox="0 0 10 15" fill="none"
                                style={{ pointerEvents: currentPage === totalPages || totalPages === 0 ? 'none' : 'auto' }}
                            >
                                <path
                                    d="M9.67644 7.30196C9.67644 7.68532 9.53022 8.06872 9.23768 8.36128L3.43228 14.1667C2.84728 14.7517 1.89877 14.7517 1.31375 14.1667C0.72875 13.5817 0.72875 12.6332 1.31375 12.0481L6.05994 7.30196L1.31375 2.55578C0.72875 1.97078 0.72875 1.02223 1.31375 0.437193C1.89877 -0.14781 2.84728 -0.14781 3.43228 0.437193L9.23768 6.24264C9.53022 6.53517 9.67644 6.91862 9.67644 7.30196Z"
                                    fill="#9DACBD" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>


            {activeModal === 'UnassignedUsersModal' && (
                <UnassignedUsersModal
                    show={true}
                    onClose={closeModal}
                    users={modalData}
                    fetchUsers={getUsersNoPlan}
                />
            )}

            {activeModal === 'UsersByPlanDetailModal' && (
                <UsersByPlanDetailModal
                    isOpen={true}
                    onClose={closeModal}
                    user={modalData}
                />
            )}

            {activeModal === 'PlanFormModal' && (
                <PlanFormModal
                    mode={modalMode}
                    data={modalData}
                    show
                    onClose={closeModal}
                    onSuccess={() => {
                        getUsers(lastUrl, searchTerm, activeFilter);
                        getUsersNoPlan();
                    }}
                />
            )}

            {activeModal === 'DeleteModal' && (
                <InlineModal
                    show
                    title="¿Estás seguro de eliminar el plan?"
                    primaryAction={{
                        label: "Confirmar",
                        onClick: deleteUser,
                    }}
                    secondaryAction={{
                        label: "Cancelar",
                        onClick: closeModal
                    }}
                />
            )}

            {activeModal === 'BiometricRegistrationModal' && (
                <BiometricCaptureModal
                    show={true}
                    onClose={closeModal}
                    user={modalData}
                />
            )}

        </>
    );
};

export default UsersByPlanPanel;
